import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TaskListComponent } from '../task-list/task-list.component';
import { TaskCalendarViewComponent } from '../task-calendar-view/task-calendar-view.component';
import { TaskTableViewComponent } from '../task-table-view/task-table-view.component';
import { Task } from '../task';
import { TaskService } from '../task.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TaskComponent } from '../task/task.component';
import { QuickTaskComponent } from '../quick-task/quick-task.component';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import { User } from '@app/_models';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { TagJoinService } from '@app/_components/_helpers/tag-join/tag-join.service';
import { Location } from '@angular/common';

export enum ViewMode {
	LIST = 'list',
	BOARD = 'board',
	CALENDAR = 'calendar',
	GANTT = 'gantt',
	TABLE = 'table'
}

@Component({
    standalone: false,
    selector: 'app-task-home',
    templateUrl: './task-home.component.html',
    styleUrls: ['./task-home.component.css'],
    providers: [MessageService]
})
export class TaskHomeComponent implements OnInit {

	// data
	selectedTag: any;
	@Input() isFolderNav = true;
	
	// View mode
	viewMode: ViewMode = ViewMode.LIST;
	ViewMode = ViewMode; // Expose enum to template
	
	// Task data shared between views
	tasks: Task[] = [];
	
	// Loading state
	filterLoading = false;
	summaryLoading = false; // Loading state for summary refresh
	
	// Grouping and filtering
	groupBy: string = 'none';
	filters: any = {
		status: [1, 2] // Default: In Progress (1) and Not Started (2)
	};
	// GroupBy options for board view (excluding assignee)
	boardGroupByOptions: { value: string; label: string }[] = [
		{ value: 'status', label: 'Status' },
		{ value: 'priority', label: 'Priority' }
	];
	currentUser: User;
	private dataLoaded: boolean = false; // Flag to prevent duplicate API calls
	
	// People filter
	usersSearch: any[] = [];
	selectedUsers: any[] = [];
	
	// Task summary/analytics
	private taskSummary: any = {};
	private taskSummarySubscription: Subscription;
	private calendarAnalytics: any = null; // Analytics from calendar view
	taskSummaryConfig: any[] = [
		{ label: 'Assigned to me', param: 'me', property: 'taskForUser' },
		{ label: 'Due for Today', param: 'today', property: 'taskToday' },
		{ label: 'Due for this week', param: 'week', property: 'taskThisWeek' },
		{ label: 'Due for this Month', param: 'month', property: 'taskThisMonth' },
		{ label: 'Follow up by me', param: 'followupme', property: 'taskForUser' },
		{ label: 'Follow up Today', param: 'followuptoday', property: 'taskFollowupToday' },
		{ label: 'Follow up this week', param: 'followupweek', property: 'taskFollowupThisWeek' },
		{ label: 'Follow up Month', param: 'followupmonth', property: 'taskFollowupThisMonth' }
	];

	summaryColumns = [
		{
			header: 'Tasks',
			rows: [
				{ label: 'All Tasks', param: 'alltasks', keys: ['taskAll'] },
				{ label: 'Created by me', param: 'icreated', keys: ['taskCreatedByMe'] },
				{ label: 'Assigned to me', param: 'iassigned', keys: ['taskAssignedToMe'] },
				{ label: 'Requested by me', param: 'irequested', keys: ['taskRequestedByMe'] }
			]
		},
		{
			header: 'Follow-up',
			rows: [
				{ label: 'All Follow-ups', param: 'followupall', keys: ['taskFollowupAll'] },
				{ label: 'Today', param: 'followuptoday', keys: ['taskFollowupToday'] },
				{ label: 'This week', param: 'followupweek', keys: ['taskFollowupThisWeek'] },
				{ label: 'This month', param: 'followupmonth', keys: ['taskFollowupThisMonth'] }
			]
		},
		{
			header: 'Due',
			rows: [
				{ label: 'All Due', param: 'dueall', keys: ['taskDueAll'] },
				{ label: 'Today', param: 'today', keys: ['taskToday'] },
				{ label: 'This week', param: 'week', keys: ['taskThisWeek'] },
				{ label: 'This month', param: 'month', keys: ['taskThisMonth'] }
			]
		}
	];
	summaryRowIndexes = [0, 1, 2, 3];
	
	statuses: any[] = [];
	priorities: any[] = [];
	
	getTaskSummaryItems(): any[] {
		const analytics = (this.viewMode === ViewMode.CALENDAR && this.calendarAnalytics) 
			? this.calendarAnalytics 
			: this.taskSummary;
		
		return this.taskSummaryConfig.map(item => ({
			...item,
			value: analytics?.[item.property] || 0
		}));
	}
	
	get activeFilterChips(): { key: string; display: string }[] {
		const chips: { key: string; display: string }[] = [];
		const f = this.filters || {};
		
		// Assigned To
		if (f.assignedTo) {
			const users = Array.isArray(f.assignedTo) ? f.assignedTo : [f.assignedTo];
			const names = users.map((u: any) => this.getUserFullName(u)).filter((n: string) => !!n).join(', ');
			if (names) {
				chips.push({
					key: 'assignedTo',
					display: `<strong>Assigned To : </strong> ${names}`
				});
			}
		}
		
		// Status
		if (Array.isArray(f.status) && f.status.length > 0) {
			const statusNames = f.status
				.map((id: number) => this.statuses.find((s: any) => s.value === id)?.name)
				.filter((n: string) => !!n)
				.join(', ');
			if (statusNames) {
				chips.push({
					key: 'status',
					display: `<strong>Status : </strong> ${statusNames}`
				});
			}
		}
		
		// Priority
		if (Array.isArray(f.priority) && f.priority.length > 0) {
			const priorityNames = f.priority
				.map((id: number) => this.priorities.find((p: any) => p.value === id)?.name)
				.filter((n: string) => !!n)
				.join(', ');
			if (priorityNames) {
				chips.push({
					key: 'priority',
					display: `<strong>Priority : </strong> ${priorityNames}`
				});
			}
		}
		
		// Category / Tags (taskId from tag selection)
		if (f.taskId) {
			const label = this.selectedTag ? this.selectedTag.name : 'Tags';
			chips.push({
				key: 'taskId',
				display: `<strong>Category : </strong> ${label}`
			});
		}
		
		// Due Date range
		if (Array.isArray(f.dateDue) && f.dateDue.length === 2 && f.dateDue[0] && f.dateDue[1]) {
			const from = new Date(f.dateDue[0]).toLocaleDateString();
			const to = new Date(f.dateDue[1]).toLocaleDateString();
			chips.push({
				key: 'dateDue',
				display: `<strong>Due Date : </strong> ${from} - ${to}`
			});
		}
		
		// Followup Date range
		if (Array.isArray(f.dateToFollowup) && f.dateToFollowup.length === 2 && f.dateToFollowup[0] && f.dateToFollowup[1]) {
			const from = new Date(f.dateToFollowup[0]).toLocaleDateString();
			const to = new Date(f.dateToFollowup[1]).toLocaleDateString();
			chips.push({
				key: 'dateToFollowup',
				display: `<strong>Followup Date : </strong> ${from} - ${to}`
			});
		}

		if (Array.isArray(f.dateCreated) && f.dateCreated.length === 2 && f.dateCreated[0] && f.dateCreated[1]) {
			const from = new Date(f.dateCreated[0]).toLocaleDateString();
			const to = new Date(f.dateCreated[1]).toLocaleDateString();
			chips.push({
				key: 'dateCreated',
				display: `<strong>Created Date : </strong> ${from} - ${to}`
			});
		}

		if (Array.isArray(f.dateCompleted) && f.dateCompleted.length === 2 && f.dateCompleted[0] && f.dateCompleted[1]) {
			const from = new Date(f.dateCompleted[0]).toLocaleDateString();
			const to = new Date(f.dateCompleted[1]).toLocaleDateString();
			chips.push({
				key: 'dateCompleted',
				display: `<strong>Completed Date : </strong> ${from} - ${to}`
			});
		}
		
		// Name search
		if (f.name) {
			chips.push({
				key: 'name',
				display: `<strong>Name : </strong> ${f.name}`
			});
		}
		
		// Created By
		if (f.createdBy) {
			const users = Array.isArray(f.createdBy) ? f.createdBy : [f.createdBy];
			const names = users.map((u: any) => this.getUserFullName(u)).filter((n: string) => !!n).join(', ');
			if (names) {
				chips.push({
					key: 'createdBy',
					display: `<strong>Author : </strong> ${names}`
				});
			}
		}
		
		// Requested By
		if (f.requestedBy) {
			const users = Array.isArray(f.requestedBy) ? f.requestedBy : [f.requestedBy];
			const names = users.map((u: any) => this.getUserFullName(u)).filter((n: string) => !!n).join(', ');
			if (names) {
				chips.push({
					key: 'requestedBy',
					display: `<strong>Requested By:</strong> ${names}`
				});
			}
		}
		
		return chips;
	}
	
	columns: any[] = [
		{ field: 'dateDue', header: 'Due Date', width: '10%', visible: true },
		{ field: 'name', header: 'Name', width: '20%', visible: true },
		{ field: 'assignedToUserVO.lastName', header: 'Assigned To', width: '10%', visible: true },
		// { field: 'hoursEstToComplete', header: 'Est Hrs', width: '10%', visible: true },
		{ field: 'priority', header: 'Priority', width: '15%', visible: true },
		{ field: 'createdByUserVO.lastName', header: 'Author', width: '10%', visible: true },
		{ field: 'requestedByUserVO.lastName', header: 'RequestedBy', width: '10%', visible: true },
		{ field: 'status', header: 'Status', width: '15%', visible: true },
		{ field: 'dateToFollowup', header: 'Followup Date', width: '12%', visible: true },
		{ field: 'dateCreated', header: 'Created', width: '10%', visible: true },
		{ field: 'dateCompleted', header: 'Completed', width: '12%', visible: true }
	];
	
	@ViewChild(TaskListComponent) taskListComponent: TaskListComponent;
	@ViewChild(TaskCalendarViewComponent) taskCalendarComponent: TaskCalendarViewComponent;
	@ViewChild(TaskTableViewComponent) taskTableComponent: TaskTableViewComponent;
	@ViewChild('taskBoardComponent') taskBoardComponent: any;

	constructor(
		private messageService: MessageService,
		private dialog: MatDialog,
		private authenticationService: AuthenticationService,
		private userService: UserService,
		private alertService: AlertService,
		private taskService: TaskService,
		private tagJoinService: TagJoinService,
		private location: Location
	) {
		// Get current user
		this.currentUser = this.authenticationService.currentUserValue;
		
		// Load status/priority metadata for filter chips
		this.statuses = this.taskService.statuses || [];
		this.priorities = this.taskService.priorities || [];
		
		// If user is already available, load data immediately
		if (this.currentUser) {
			this.loadUsers();
			this.refreshSummaryCounts();
			this.dataLoaded = true;
		}
		
		// Subscribe to user changes for future updates
		this.authenticationService.currentUser.subscribe(user => {
			if (user) {
				// Only load if user changed (different userId) and we haven't loaded yet, or if user actually changed
				const userIdChanged = !this.currentUser || this.currentUser.userId !== user.userId;
				if (userIdChanged && !this.dataLoaded) {
					this.currentUser = user;
					this.loadUsers();
					this.refreshSummaryCounts();
					this.dataLoaded = true;
				} else if (userIdChanged) {
					// User changed after initial load
					this.currentUser = user;
					this.loadUsers();
					this.refreshSummaryCounts();
				} else {
					this.currentUser = user;
				}
			}
		});
	}

	ngOnInit(): void {
		// Columns are initialized in the property declaration above
		// They will be synced with task-view-controls component
		// Ensure columns have proper structure
		if (!this.columns || this.columns.length === 0) {
			this.columns = [
				{ field: 'dateDue', header: 'Due Date', width: '10%', visible: true },
				{ field: 'name', header: 'Name', width: '20%', visible: true },
				{ field: 'assignedToUserVO.lastName', header: 'Assigned To', width: '10%', visible: true },
				// { field: 'hoursEstToComplete', header: 'Est Hrs', width: '10%', visible: true },
				{ field: 'priority', header: 'Priority', width: '15%', visible: true },
				{ field: 'createdByUserVO.lastName', header: 'Author', width: '10%', visible: true },
				{ field: 'requestedByUserVO.lastName', header: 'RequestedBy', width: '10%', visible: true },
				{ field: 'status', header: 'Status', width: '15%', visible: true },
				{ field: 'dateToFollowup', header: 'Followup Date', width: '12%', visible: true },
				{ field: 'dateCreated', header: 'Created', width: '10%', visible: true },
				{ field: 'dateCompleted', header: 'Completed', width: '12%', visible: true }
			];
		}
		
		// Apply default filter and load — security layer already restricts to tasks visible to this user
		if (this.currentUser) {
			this.filters = { status: [1, 2], requestedBy: this.currentUser };
			setTimeout(() => {
				this.applyFiltersAndGrouping();
			}, 100);
		}
	}

	private getSummaryAnalytics(): any {
		return (this.viewMode === ViewMode.CALENDAR && this.calendarAnalytics)
			? this.calendarAnalytics
			: this.taskSummary;
	}

	getSummaryValue(keys: string[]): number | string {
		const analytics = this.getSummaryAnalytics() || {};
		for (const key of keys) {
			const value = analytics?.[key];
			if (typeof value === 'number') {
				return value;
			}
		}
		return 0;
	}

	isSummaryLinkAvailable(keys: string[]): boolean {
		// return this.getSummaryValue(keys) !== 'NA';
		return true;
	}

	onTagSelect(event) {
		this.selectedTag = event;

		if (this.selectedTag) {
			this.filterLoading = true;
			this.tagJoinService.findTagsWithType(this.selectedTag?.name, 40, this.selectedTag?.tagType).pipe(first()).subscribe(
				res => {
					console.log(res.data.tagJoin);
					this.filterLoading = false;
					this.getFilteredTags(res.data.tagJoin, this.selectedTag);
				},
				error => {
					this.alertService.error(error);
					this.filterLoading = false;
				}
			);
		} else {
			this.getFilteredTags('0', this.selectedTag);
		}
	}

	getFilteredTags(tags, selectedTag) {
		// Show loading indicator
		this.filterLoading = true;
		
		// if filter text input is empty then load all records
		if (!selectedTag) {
			this.selectedTag = null;
		}
		
		if (tags == '0') {
			// Clear taskId filter
			if (this.taskListComponent && this.taskListComponent.lazyLoadEvent && this.taskListComponent.lazyLoadEvent.filters) {
				delete this.taskListComponent.lazyLoadEvent.filters.taskId;
			}
			// Also clear from our filters object
			const newFilters = { ...this.filters };
			delete newFilters.taskId;
			this.filters = newFilters;
			this.applyFiltersAndGrouping();
		} else {
			// If records found then show filtered records
			if (tags.length > 0) {
				// Extract task IDs from tag joins
				let retrievedTasks = [];
				tags.forEach(tag => {
					retrievedTasks.push(tag.tagSourceId);
				});
				
				// Apply filter to task list component
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent) {
					if (!this.taskListComponent.lazyLoadEvent.filters) {
						this.taskListComponent.lazyLoadEvent.filters = {};
					}
					this.taskListComponent.lazyLoadEvent.filters.taskId = { 'value': retrievedTasks, 'matchMode': 'in' };
				}
				
				// Also update our filters object
				this.filters = {
					...this.filters,
					taskId: retrievedTasks
				};
				
				this.applyFiltersAndGrouping();
			} else {
				// Display empty table as no record found with filtered input
				this.tasks = [];
				if (this.taskListComponent) {
					this.taskListComponent.tasks = [];
					this.taskListComponent.taskList = [];
					this.taskListComponent.recordTotal = 0;
				}
				// Hide loading when no tasks found
				this.filterLoading = false;
			}
		}
	}
	
	onViewModeChange(mode: ViewMode) {
		this.viewMode = mode;
		// Clear calendar analytics when switching away from calendar view
		if (mode !== ViewMode.CALENDAR) {
			this.calendarAnalytics = null;
		}
		
		// For board view, ensure groupBy is set to 'status' if it's 'none'
		if (mode === ViewMode.BOARD && (this.groupBy === 'none' || !this.groupBy)) {
			this.groupBy = 'status';
		}
		
		// Show loading when switching views
		this.filterLoading = true;
		
		// Each view will load its own tasks dynamically based on filters
		// Trigger reload by updating filters (which will trigger ngOnChanges in views)
		// Create a new object reference to ensure change detection
		this.filters = { ...this.filters };
		
		// For list view, also trigger lazy load
		if (mode === ViewMode.LIST && this.taskListComponent) {
			setTimeout(() => {
				this.applyFiltersAndGrouping();
			}, 100);
		} else {
			// For other views, they will reload via ngOnChanges when filters change
			// Hide loading after a short delay to allow views to process
			setTimeout(() => {
				this.filterLoading = false;
			}, 300);
		}
	}
	
	onTasksLoaded(tasks: Task[]) {
		this.tasks = tasks;
		// Hide loading when tasks are loaded (for list view)
		if (this.viewMode === ViewMode.LIST) {
			this.filterLoading = false;
		}
	}

	onFiltersProcessed() {
		this.filterLoading = false;
	}
	
	onTaskClick(task: Task) {
		this.openTaskDialog(task);
	}
	
	onTaskDoubleClick(task: Task) {
		this.openTaskDialog(task);
	}
	
	private openTaskDialog(task: Task) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = "100vw";
		dialogConfig.width = "100vw";
		dialogConfig.maxHeight = "100vh";
		dialogConfig.height = "100vh";
		dialogConfig.data = {
			task: task,
			title: '',
			taskApi: task,
			isModal: true
		};
		
		// URL is already changed in base component's onTaskClick
		const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
		
		// Handle task updates and reset URL
		if (dialogRef.componentInstance) {
			dialogRef.componentInstance.doUpdate.subscribe(
				data => {
					console.log('Task updated:', data);
					this.location.go('/');
				});
			dialogRef.componentInstance.doAdd.subscribe(
				data => {
					console.log('Task added:', data);
					this.location.go('/');
				});
		}
		
		dialogRef.afterClosed().subscribe(result => {
			// Reset URL when dialog closes
			this.location.go('/');
			// Refresh tasks for active view
			if (this.taskTableComponent) {
				this.taskTableComponent.reloadTasksFromParent();
			} else if (this.taskListComponent) {
				this.taskListComponent.loadLazy(this.taskListComponent.lazyLoadEvent);
			} else if (this.taskBoardComponent) {
				this.taskBoardComponent.reloadTasks();
			}
		});
	}

	trackByChipKey(_index: number, chip: { key: string }): string {
		return chip.key;
	}

	removeFilterChip(key: string, event?: MouseEvent) {
		event?.stopPropagation();
		const newFilters: any = { ...this.filters };
		
		switch (key) {
			case 'assignedTo':
				delete newFilters.assignedTo;
				this.selectedUsers = [];
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['assignedTo'];
					delete this.taskListComponent.lazyLoadEvent.filters['assignedToUserVO.userId'];
					this.taskListComponent.assignedSearchToUser = null;
				}
				break;
			case 'status':
				delete newFilters.status;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['status'];
					this.taskListComponent.selectedStatusFilter = [];
				}
				break;
			case 'priority':
				delete newFilters.priority;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['priority'];
					this.taskListComponent.selectedPriorityFilter = [];
				}
				break;
			case 'taskId':
				delete newFilters.taskId;
				this.selectedTag = null;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['taskId'];
				}
				break;
			case 'dateDue':
				delete newFilters.dateDue;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['dateDue'];
				}
				break;
			case 'dateToFollowup':
				delete newFilters.dateToFollowup;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['dateToFollowup'];
				}
				break;
			case 'dateCreated':
				delete newFilters.dateCreated;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['dateCreated'];
				}
				break;
			case 'dateCompleted':
				delete newFilters.dateCompleted;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['dateCompleted'];
				}
				break;
			case 'name':
				delete newFilters.name;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['name'];
					this.taskListComponent.selectedFilterName = '';
				}
				break;
			case 'createdBy':
				delete newFilters.createdBy;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['createdByUserVO.userId'];
					this.taskListComponent.createdBySearchToUser = null;
				}
				break;
			case 'requestedBy':
				delete newFilters.requestedBy;
				if (this.taskListComponent && this.taskListComponent.lazyLoadEvent?.filters) {
					delete this.taskListComponent.lazyLoadEvent.filters['requestedByUserVO.userId'];
					this.taskListComponent.requestedBySearchToUser = null;
				}
				break;
			default:
				delete newFilters[key];
				break;
		}
		
		this.filters = newFilters;
		if (this.taskListComponent) {
			this.filterLoading = true;
		}
		this.applyFiltersAndGrouping();
	}
	
	private getUserFullName(user: any): string {
		if (!user) {
			return '';
		}
		if (user.fullName) {
			return user.fullName;
		}
		const first = user.firstName || '';
		const last = user.lastName || '';
		return `${first} ${last}`.trim();
	}
	
	getCalendarTaskCount(): number {
		return this.tasks ? this.tasks.length : 0;
	}
	
	onTaskCreated() {
		// Refresh task list when a new task is created
		if (this.viewMode === ViewMode.LIST && this.taskListComponent) {
			if (this.taskListComponent.lazyLoadEvent) {
				this.taskListComponent.loadLazy(this.taskListComponent.lazyLoadEvent);
			}
		} else if (this.viewMode === ViewMode.BOARD && this.taskBoardComponent) {
			// Board view handles its own reload - call reloadTasks if available
			if (this.taskBoardComponent.reloadTasks) {
				this.taskBoardComponent.reloadTasks();
			}
		}
		// Refresh summary counts
		if (this.currentUser) {
			this.taskService.getTaskSummary(this.currentUser.userId).pipe(first()).subscribe();
		}
	}
	
	onGroupByChange(groupBy: string) {
		this.groupBy = groupBy;
		// Show loading indicator
		this.filterLoading = true;
		// Apply grouping to all views
		// Each view will detect the groupBy change via ngOnChanges and reload dynamically
		if (this.viewMode === ViewMode.LIST && this.taskListComponent) {
			// For list view, reload tasks and apply grouping
			this.applyFiltersAndGrouping();
		} else {
			// For other views, they will handle grouping via ngOnChanges
			// Trigger change detection by updating filters reference
			this.filters = { ...this.filters };
			// Hide loading after a delay to allow views to process
			setTimeout(() => {
				this.filterLoading = false;
			}, 300);
		}
	}
	
	onFiltersChange(filters: any) {
		try {
			if (JSON.stringify(filters) === JSON.stringify(this.filters)) {
				return;
			}
		} catch (_) {}
		// Create a new object reference to ensure change detection
		this.filters = { ...filters };
		// Show loading indicator
		this.filterLoading = true;
		// Apply filters to all views
		// Each view will detect the filter change via ngOnChanges and reload dynamically
		this.applyFiltersAndGrouping();
	}

	onListFiltersChanged(listFilters: any) {
		// Show loading indicator
		this.filterLoading = true;
		
		// Merge list view filters with existing filters
		// This handles filters applied directly in the list view (table filters)
		const mergedFilters = { ...this.filters };
		
		// Convert list view filter keys to match our filter format
		Object.keys(listFilters).forEach(key => {
			if (key === 'assignedToUserVO.userId') {
				// Convert to assignedTo format
				const userIds = Array.isArray(listFilters[key]) ? listFilters[key] : [listFilters[key]];
				if (userIds.length > 0) {
					const users = this.usersSearch.filter(u => userIds.includes(u.userId));
					if (users.length === 1) {
						mergedFilters.assignedTo = users[0];
					} else if (users.length > 1) {
						mergedFilters.assignedTo = users;
					}
				} else {
					delete mergedFilters.assignedTo;
				}
			} else if (key === 'createdByUserVO.userId') {
				// Convert to createdBy format
				const userIds = Array.isArray(listFilters[key]) ? listFilters[key] : [listFilters[key]];
				if (userIds.length > 0) {
					const users = this.usersSearch.filter(u => userIds.includes(u.userId));
					if (users.length === 1) {
						mergedFilters.createdBy = users[0];
					} else if (users.length > 1) {
						mergedFilters.createdBy = users;
					}
				} else {
					delete mergedFilters.createdBy;
				}
			} else if (key === 'requestedByUserVO.userId') {
				// Convert to requestedBy format
				const userIds = Array.isArray(listFilters[key]) ? listFilters[key] : [listFilters[key]];
				if (userIds.length > 0) {
					const users = this.usersSearch.filter(u => userIds.includes(u.userId));
					if (users.length === 1) {
						mergedFilters.requestedBy = users[0];
					} else if (users.length > 1) {
						mergedFilters.requestedBy = users;
					}
				} else {
					delete mergedFilters.requestedBy;
				}
			} else if (key === 'taskId') {
				// Keep taskId as is (from category filter)
				if (listFilters[key] && Array.isArray(listFilters[key]) && listFilters[key].length > 0) {
					mergedFilters.taskId = listFilters[key];
				} else {
					delete mergedFilters.taskId;
				}
			} else {
				// Keep other filters as is
				if (listFilters[key] !== null && listFilters[key] !== undefined && listFilters[key] !== '') {
					mergedFilters[key] = listFilters[key];
				} else {
					delete mergedFilters[key];
				}
			}
		});
		
		// Update filters - this will trigger ngOnChanges in all child views
		// Create a new object reference to ensure change detection
		this.filters = { ...mergedFilters };
		
		// Update selectedUsers to match filters
		if (mergedFilters.assignedTo) {
			if (Array.isArray(mergedFilters.assignedTo)) {
				this.selectedUsers = mergedFilters.assignedTo.map(u => u.userId);
			} else {
				this.selectedUsers = [mergedFilters.assignedTo.userId];
			}
		} else {
			this.selectedUsers = [];
		}
		
		// If in list view, loading will be hidden when tasksLoaded is called
		// If not in list view, loading will be hidden when filtersProcessed is emitted
	}
	
	onColumnsChange(columns: any[]) {
		// Force change detection by creating a new array reference with new objects
		this.columns = columns.map(col => ({ ...col }));
		// The list view will detect this change via ngOnChanges
		// No need to reload tasks, just update column visibility
	}
	
	private applyFiltersAndGrouping() {
		// Refresh task list with new filters
		if (this.taskListComponent && this.viewMode === ViewMode.LIST) {
			// Initialize lazyLoadEvent if it doesn't exist
			if (!this.taskListComponent.lazyLoadEvent) {
				this.taskListComponent.lazyLoadEvent = { first: 0, rows: 25, sortField: 'dateCreated', sortOrder: -1 };
			}

			// Update filters in task list component
			if (!this.taskListComponent.lazyLoadEvent.filters) {
				this.taskListComponent.lazyLoadEvent.filters = {};
			}

			const parentManagedLazyKeys = [
				'status', 'priority', 'name', 'dateDue', 'dateToFollowup',
				'dateCreated', 'dateCompleted', 'taskId',
				'assignedToUserVO.userId', 'createdByUserVO.userId', 'requestedByUserVO.userId'
			];
			parentManagedLazyKeys.forEach(k => delete this.taskListComponent.lazyLoadEvent.filters[k]);

			// Reset list-component UI state for filters that are no longer active
			if (!this.filters.assignedTo) {
				this.taskListComponent.assignedSearchToUser = null;
			}
			if (!this.filters.createdBy) {
				this.taskListComponent.createdBySearchToUser = null;
			}
			if (!this.filters.requestedBy) {
				this.taskListComponent.requestedBySearchToUser = null;
			}
			if (!this.filters.status) {
				this.taskListComponent.selectedStatusFilter = [];
			}
			if (!this.filters.priority) {
				this.taskListComponent.selectedPriorityFilter = [];
			}
			if (!this.filters.name) {
				this.taskListComponent.selectedFilterName = '';
			}

			// Re-apply current filters
			Object.keys(this.filters).forEach(key => {
				if (this.filters[key] !== null && this.filters[key] !== '' && 
				    (!Array.isArray(this.filters[key]) || this.filters[key].length > 0)) {
					// Special handling for taskId filter (from category selection)
					if (key === 'taskId' && Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters[key] = {
							value: this.filters[key],
							matchMode: 'in'
						};
					} 
					else if (key === 'dateDue' && Array.isArray(this.filters[key]) && this.filters[key].length === 2) {
						const dateRange = [
							this.filters[key][0].toISOString().split('T')[0],
							this.filters[key][1].toISOString().split('T')[0]
						];
						this.taskListComponent.lazyLoadEvent.filters['dateDue'] = {
							value: dateRange,
							matchMode: 'between'
						};
					}
					else if (key === 'dateToFollowup' && Array.isArray(this.filters[key]) && this.filters[key].length === 2) {
						const dateRange = [
							this.filters[key][0].toISOString().split('T')[0],
							this.filters[key][1].toISOString().split('T')[0]
						];
						this.taskListComponent.lazyLoadEvent.filters['dateToFollowup'] = {
							value: dateRange,
							matchMode: 'between'
						};
					}
					else if (key === 'assignedTo' && typeof this.filters[key] === 'object' && !Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['assignedToUserVO.userId'] = {
							value: this.filters[key].userId,
							matchMode: 'in'
						};
						this.taskListComponent.assignedSearchToUser = this.filters[key];
					}
					else if (key === 'assignedTo' && Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['assignedToUserVO.userId'] = {
							value: this.filters[key].map((u: any) => u.userId),
							matchMode: 'in'
						};
						this.taskListComponent.assignedSearchToUser = this.filters[key].length === 1 ? this.filters[key][0] : null;
					}
					else if (key === 'requestedBy' && typeof this.filters[key] === 'object' && !Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['requestedByUserVO.userId'] = {
							value: [this.filters[key].userId],
							matchMode: 'in'
						};
						this.taskListComponent.requestedBySearchToUser = this.filters[key];
					}
					else if (key === 'requestedBy' && Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['requestedByUserVO.userId'] = {
							value: this.filters[key].map((u: any) => u.userId),
							matchMode: 'in'
						};
						this.taskListComponent.requestedBySearchToUser = this.filters[key].length === 1 ? this.filters[key][0] : null;
					}
					else if (key === 'createdBy' && typeof this.filters[key] === 'object' && !Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['createdByUserVO.userId'] = {
							value: [this.filters[key].userId],
							matchMode: 'in'
						};
						this.taskListComponent.createdBySearchToUser = this.filters[key];
					}
					else if (key === 'createdBy' && Array.isArray(this.filters[key])) {
						this.taskListComponent.lazyLoadEvent.filters['createdByUserVO.userId'] = {
							value: this.filters[key].map((u: any) => u.userId),
							matchMode: 'in'
						};
						this.taskListComponent.createdBySearchToUser = this.filters[key].length === 1 ? this.filters[key][0] : null;
					}
					else {
						this.taskListComponent.lazyLoadEvent.filters[key] = {
							value: this.filters[key],
							matchMode: Array.isArray(this.filters[key]) ? 'in' : 'contains'
						};
					}
				} else {
					delete this.taskListComponent.lazyLoadEvent.filters[key];
					if (key === 'assignedTo') {
						delete this.taskListComponent.lazyLoadEvent.filters['assignedToUserVO.userId'];
						this.taskListComponent.assignedSearchToUser = null;
					}
					if (key === 'requestedBy') {
						delete this.taskListComponent.lazyLoadEvent.filters['requestedByUserVO.userId'];
						this.taskListComponent.requestedBySearchToUser = null;
					}
					if (key === 'createdBy') {
						delete this.taskListComponent.lazyLoadEvent.filters['createdByUserVO.userId'];
						this.taskListComponent.createdBySearchToUser = null;
					}
				}
			});
			
			// Load tasks with updated filters
			this.taskListComponent.loadLazy(this.taskListComponent.lazyLoadEvent);
		}
		// For non-list views, they will emit filtersProcessed when done
		// Loading will be hidden via onFiltersProcessed()
	}

	loadUsers() {
		this.userService.findUsersWith('').pipe(first()).subscribe(
			data => {
				this.usersSearch = [];
				this.usersSearch = data.data.userSummaryList;
				this.usersSearch.forEach(user => {
					if (!user.lastName) { user.lastName = ''; }
					if (!user.firstName) { user.firstName = ''; }
					user.fullName = user.lastName + ' ' + user.firstName;
				});
			},
			error => {
				this.alertService.error(error);
			}
		);
	}

	onPeopleSelectFromNav(user: any) {
		if (user?.userId) {
			this.selectedTag = null;
			// Clear all user-specific filters so the People nav click isn't ANDed with stale defaults
			const newFilters: any = { status: this.filters.status || [1, 2] };
			this.filters = newFilters;
			this.selectedUsers = [user.userId];
			this.onPeopleFilterChange(this.selectedUsers);
		}
	}

	onPeopleFilterChange(selectedUserIds: any[]) {
		this.selectedUsers = selectedUserIds;
		// Show loading indicator
		this.filterLoading = true;
		// Update filters with selected users
		if (selectedUserIds && selectedUserIds.length > 0) {
			// Find user objects from selected IDs
			const selectedUserObjects = this.usersSearch.filter(u => selectedUserIds.includes(u.userId));
			if (selectedUserObjects.length > 0) {
				this.filters = {
					...this.filters,
					assignedTo: selectedUserObjects.length === 1 ? selectedUserObjects[0] : selectedUserObjects
				};
			}
		} else {
			// Clear assignedTo filter
			const newFilters = { ...this.filters };
			delete newFilters.assignedTo;
			this.filters = newFilters;
		}
		this.applyFiltersAndGrouping();
	}

	private refreshSummaryCounts() {
		if (!this.currentUser) return;
		
		this.taskSummarySubscription = this.taskService.currentTaskSummary.subscribe(taskSummary => {
			this.taskSummary = taskSummary;
		});
		
		this.taskService.getTaskSummary(this.currentUser.userId).pipe(first()).subscribe();
	}

	private formatDateYYYYMMDD(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	loadWithParam(param: string) {
		if (!this.currentUser) return;
		
		this.filterLoading = true;
		
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const endOfToday = new Date();
		endOfToday.setHours(23, 59, 59, 999);
		
		const startOfWeek = new Date(today);
		const dayOfWeek = startOfWeek.getDay();
		const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust to Monday
		startOfWeek.setDate(diff);
		startOfWeek.setHours(0, 0, 0, 0);
		
		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6);
		endOfWeek.setHours(23, 59, 59, 999);
		
		const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
		startOfMonth.setHours(0, 0, 0, 0);
		const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
		endOfMonth.setHours(23, 59, 59, 999);
		const minDate = new Date(1970, 0, 1);
		minDate.setHours(0, 0, 0, 0);
		const maxDate = new Date(2099, 11, 31);
		maxDate.setHours(23, 59, 59, 999);
		
		const newFilters: any = { ...this.filters };
		
		switch (param) {
			case 'alltasks':
				this.clearOtherFilters(newFilters, ['assignedTo']);
				break;

			case 'me':
				newFilters.assignedTo = this.currentUser;
				this.clearOtherFilters(newFilters,['assignedTo']);
				break;

			case 'icreated':
				newFilters.createdBy = this.currentUser;
				this.clearOtherFilters(newFilters,['createdBy']);
				break;

			case 'iassigned':
				newFilters.assignedTo = this.currentUser;
				this.clearOtherFilters(newFilters,['assignedTo']);
				break;

			case 'irequested':
				newFilters.requestedBy = this.currentUser;
				this.clearOtherFilters(newFilters,['requestedBy']);
				break;

			case 'followupme':
				newFilters.requestedBy = this.currentUser;
				this.clearOtherFilters(newFilters,['requestedBy']);
				break;

			case 'followupall':
				newFilters.dateToFollowup = [new Date(minDate), new Date(maxDate)];
				this.clearOtherFilters(newFilters,['dateToFollowup']);
				break;

			case 'dueall':
				newFilters.dateDue = [new Date(minDate), new Date(maxDate)];
				this.clearOtherFilters(newFilters,['dateDue']);
				break;
				
			case 'today':
				newFilters.dateDue = [new Date(today), new Date(endOfToday)];
				this.clearOtherFilters(newFilters,['dateDue']);
				break;
				
			case 'week': {
				newFilters.dateDue = [new Date(startOfWeek), new Date(endOfWeek)];
				this.clearOtherFilters(newFilters, ['dateDue']);
				break;
			}

			case 'month': {
				newFilters.dateDue = [new Date(startOfMonth), new Date(endOfMonth)];
				this.clearOtherFilters(newFilters, ['dateDue']);
				break;
			}
				
			case 'followuptoday':
				newFilters.dateToFollowup = [new Date(today), new Date(endOfToday)];
				this.clearOtherFilters(newFilters,['dateToFollowup']);
				break;
				
			case 'followupweek': {
				newFilters.dateToFollowup = [new Date(startOfWeek), new Date(endOfWeek)];
				this.clearOtherFilters(newFilters, ['dateToFollowup']);
				break;
			}

			case 'followupmonth': {
				newFilters.dateToFollowup = [new Date(startOfMonth), new Date(endOfMonth)];
				this.clearOtherFilters(newFilters, ['dateToFollowup']);
				break;
			}
				
			default:
				console.warn('Unknown filter param:', param);
				this.filterLoading = false;
				return;
		}
		
		this.filters = newFilters;
		
		if (newFilters.assignedTo) {
			if (Array.isArray(newFilters.assignedTo)) {
				this.selectedUsers = newFilters.assignedTo.map(u => u.userId);
			} else {
				this.selectedUsers = [newFilters.assignedTo.userId];
			}
		} else {
			this.selectedUsers = [];
		}
		
		this.applyFiltersAndGrouping();
	}

	private clearOtherFilters(filters: any,exclude: string[]) {
		Object.keys(filters).forEach(key => {
			if (exclude.includes(key)) {
				return;
			}
			filters?.[key] ? delete filters[key] : null;
		});
	}

	onCalendarAnalyticsUpdate(analytics: any) {
		// Update calendar analytics when calendar view emits new analytics
		this.calendarAnalytics = analytics;
	}

	reloadSummaryCount() {
		if (this.currentUser) {
			this.summaryLoading = true;
			this.taskService.getTaskSummary(this.currentUser.userId).pipe(first()).subscribe(
				() => {
					// Summary refreshed successfully
					this.summaryLoading = false;
				},
				(error) => {
					// Handle error
					this.alertService.error(error);
					this.summaryLoading = false;
				}
			);
		}
	}

	addTask(tasktypeId: any) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = '100vw';
		dialogConfig.height = '100vh';
		dialogConfig.maxWidth = '100vw';
		dialogConfig.maxHeight = '100vh';
		dialogConfig.data = {
			title: 'Add Task',
			taskTypeId: tasktypeId,
			isModal: true,
			selectedTagForCreate: this.selectedTag
		};

		const dialogRef = this.dialog.open(TaskComponent, dialogConfig);

		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.onTaskCreated();
			});
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.onTaskCreated();
			});
	}

	addQuickTask(tasktypeId: any) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = 'auto';
		dialogConfig.height = 'auto';
		dialogConfig.maxWidth = '1600px';
		dialogConfig.maxHeight = '800px';
		dialogConfig.data = {
			title: 'Add Task',
			taskTypeId: tasktypeId,
			isModal: true,
			selectedTagForCreate: this.selectedTag
		};

		const dialogRef = this.dialog.open(QuickTaskComponent, dialogConfig);

		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.onTaskCreated();
				dialogRef.close();
			});
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.onTaskCreated();
			});
	}

	// Template dialog
	showTemplateDialog = false;
	templateList: any[] = [];
	selectedTemplateId: number = null;
	templateChildren: any[] = [];
	templateOverrides: any = {};
	templateInstantiating = false;

	openCreateFromTemplate() {
		this.templateOverrides = {};
		this.selectedTemplateId = null;
		this.templateChildren = [];
		this.templateInstantiating = false;
		this.taskService.getTemplates().pipe(first()).subscribe(
			res => {
				this.templateList = res.data.tasks || [];
				this.showTemplateDialog = true;
			},
			err => {
				this.alertService.error(err);
			}
		);
	}

	onTemplateSelected(templateTaskId: number) {
		this.templateChildren = [];
		if (!templateTaskId) return;
		this.taskService.findById(templateTaskId).pipe(first()).subscribe(
			res => {
				const task = res.data.task;
				this.templateChildren = task.children || [];
			},
			err => {
				this.alertService.error(err);
			}
		);
	}

	createFromTemplate() {
		if (!this.selectedTemplateId) return;
		this.templateInstantiating = true;
		this.taskService.instantiateTemplate(this.selectedTemplateId, this.templateOverrides).pipe(first()).subscribe(
			res => {
				this.templateInstantiating = false;
				this.showTemplateDialog = false;
				const newTask = res.data.task;
				this.alertService.success(res, true);
				const dialogConfig = new MatDialogConfig();
				dialogConfig.disableClose = false;
				dialogConfig.autoFocus = true;
				dialogConfig.hasBackdrop = true;
				dialogConfig.width = 'auto';
				dialogConfig.height = 'auto';
				dialogConfig.maxWidth = '1600px';
				dialogConfig.maxHeight = '800px';
				dialogConfig.data = {
					title: 'View Task',
					taskId: newTask.taskId,
					taskTypeId: newTask.taskType,
					isModal: true
				};
				const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
				dialogRef.afterClosed().subscribe(() => {
					this.onTaskCreated();
				});
			},
			err => {
				this.templateInstantiating = false;
				this.alertService.error(err);
			}
		);
	}
}
